/*
  Desktop header navigation behaviour.

  Deliberately small. The nav is Focal's <desktop-navigation> and the search is
  Focal's <predictive-search-drawer>, so the mega-menu animation, aria-expanded
  and predictive search itself are already handled. This file only covers what
  the header design needs on top:

    1. Click-to-toggle on the dropdown triggers. They are <button>s ("signal an
       action will occur, vs linking to a new page"), so there is no navigation
       to fall back on. Focal's own click handler bails out on hover-capable
       devices and lets the anchor navigate, which for a button means nothing
       happens at all, including for keyboard users, who reach the trigger with
       Tab and press Enter.
    2. Flagging when the search field has a query, so the results panel can
       appear under the pill. Focal drives the drawer's `open` attribute for
       this normally, but `open` also locks page scroll (lock-all on <html>),
       which is wrong for a suggestion panel in the header.
*/

(function () {
  'use strict';

  var SEARCH_ID = 'hnav-search-drawer';

  /* ---------- 1. Dropdown triggers ----------
     "Recommend testing click to open rather than hover to improve usability &
     accessibility" — so this file is the ONLY thing that opens a variant
     dropdown. Focal's hover-open is not suppressed here; it simply never
     matches, because the variant's <li> deliberately omits .has-dropdown, the
     sole selector its mouseenter delegate is bound to.

     That leaves Focal's openDropdown/closeDropdown unused for the variant, so
     this drives the same state they do — aria-expanded on the trigger, [hidden]
     on the panel — directly. */

  function panelFor(trigger) {
    return document.getElementById(trigger.getAttribute('aria-controls'));
  }

  function setExpanded(trigger, expanded) {
    var panel = panelFor(trigger);
    if (!panel) return;

    trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    panel.hidden = !expanded;

    // store-header listens for these to hold a transparent header opaque while
    // a dropdown is over it. Focal fires them from its own openDropdown, which
    // the variant never reaches, so they have to be fired here or a transparent
    // homepage header would show light-on-white text against the open panel.
    trigger.dispatchEvent(new CustomEvent(
      expanded ? 'desktop-nav:dropdown:open' : 'desktop-nav:dropdown:close',
      { bubbles: true }
    ));
  }

  function closeAllDropdowns(except) {
    document.querySelectorAll('.hnav-nav__trigger[aria-expanded="true"]').forEach(function (trigger) {
      if (trigger !== except) setExpanded(trigger, false);
    });
  }

  function initDropdowns() {
    var nav = document.querySelector('.hnav-nav');
    if (!nav) return;

    // Focal's desktop-navigation delegates click for
    // .header__linklist-link[aria-expanded] on this very element, and our
    // triggers match that selector. On a non-hover device it would preventDefault
    // and run its own openDropdown, fighting this handler over the same click.
    // (Its hover delegate is already inert here — see setExpanded above.)
    //
    // Listening on the CAPTURE phase of the same element is what settles it: the
    // target is a descendant, so capture here runs before Focal's bubble-phase
    // delegate, and stopPropagation then keeps the event from reaching it at all.
    // Registration order does not come into it, which matters because Focal
    // binds at custom-element upgrade and this file binds on DOMContentLoaded.
    nav.addEventListener('click', function (event) {
      var trigger = event.target.closest('.hnav-nav__trigger');
      if (!trigger) return;

      event.preventDefault();
      event.stopPropagation();

      var isOpen = trigger.getAttribute('aria-expanded') === 'true';

      closeAllDropdowns(trigger);
      setExpanded(trigger, !isOpen);

      // stopPropagation above also keeps this click from reaching the
      // document-level dismissal below, so the search panel is closed here.
      var search = document.querySelector('.hnav-search');
      if (search) search.classList.remove('hnav-searching');
    }, true);

    // A dropdown opened by keyboard never gets the mouseleave Focal closes on.
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;

      var open = document.querySelector('.hnav-nav__trigger[aria-expanded="true"]');
      if (!open) return;

      setExpanded(open, false);
      open.focus();
    });

    document.addEventListener('click', function (event) {
      if (!event.target.closest('.hnav-nav')) closeAllDropdowns();
    });
  }

  /* ---------- 2. Search panel state ----------
     Mirrors how the mobile test flags its own inline search (tmm-searching). */

  function initSearchState() {
    var drawer = document.getElementById(SEARCH_ID);
    if (!drawer) return;

    var wrapper = drawer.closest('.hnav-search');
    var input = drawer.querySelector('[name="q"]');
    if (!wrapper || !input) return;

    /* The overlay starts at the header's bottom edge so the header stays live
       above it. That edge moves with scroll — the header is sticky but the
       announcement bar above it is not, so the offset shrinks once the page has
       scrolled away from the top. Measured as the panel opens and on resize;
       measuring on scroll is unnecessary because opening is the only moment the
       overlay appears, and the sticky header does not move after that. */
    function setOverlayTop() {
      var header = document.querySelector('.shopify-section--header');
      var top = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;

      document.documentElement.style.setProperty('--hnav-header-bottom', Math.round(top) + 'px');
    }

    function sync() {
      var searching = input.value.trim() !== '';

      if (searching) setOverlayTop();
      wrapper.classList.toggle('hnav-searching', searching);
    }

    input.addEventListener('input', sync);
    window.addEventListener('resize', setOverlayTop);

    // The overlay sits inside .hnav-search, so the outside-click handler below
    // never sees it. Focal closes its own drawers this way too.
    wrapper.addEventListener('click', function (event) {
      if (event.target.closest('.drawer__overlay')) {
        wrapper.classList.remove('hnav-searching');
      }
    });

    // "Cancel" (and the no-results "New search") clear the field through
    // Focal's own handler, which does not dispatch an input event — so read the
    // field back on the next frame instead of trusting one.
    drawer.addEventListener('click', function (event) {
      if (event.target.closest('[data-action="reset-search"]')) {
        requestAnimationFrame(sync);
      }
    });

    // Clicking away from the header should dismiss the panel without wiping
    // what was typed, so the field keeps its value and only the flag drops.
    document.addEventListener('click', function (event) {
      if (!event.target.closest('.hnav-search')) {
        wrapper.classList.remove('hnav-searching');
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') wrapper.classList.remove('hnav-searching');
    });

    // Re-focusing the field should bring a still-typed query's results back.
    input.addEventListener('focus', sync);

    sync();
  }

  function init() {
    initDropdowns();
    initSearchState();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
